class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.cartUpdateUnsubscriber = undefined;

    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    if (document.querySelector(".cart-shipping-goal")) {
      this.initCartShippingGoal();
    }

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  initCartShippingGoal() {
    document.querySelectorAll(".cart-shipping-goal").forEach((el) => {
      const progressEl = el.querySelector(".js-shipping-goal-progress");
      if (!progressEl) return;

      let progressPrev =
        getComputedStyle(progressEl)?.getPropertyValue("width");
      document.documentElement.style.setProperty(
        "--shipping-goal-progress-prev",
        progressPrev
      );

      const total = el.dataset.total;
      const minSpend = el.dataset.minSpend;
      const messageTemplate = el.dataset.messageTemplate;

      const minTotal = Math.round(minSpend * (Shopify.currency.rate || 1));
      let progress = (total / minTotal) * 100;
      if (progress > 100) progress = 100;

      const msgDefault = el.querySelector("[data-shipping-message-default]");
      const msgValueEl = el.querySelector("[data-shipping-message-value]");
      const msgSuccess = el.querySelector("[data-shipping-message-success]");

      if (minTotal > total) {
        let amount = minTotal - total;
        // formatMoney - func in global.js
        let message = messageTemplate.replace("[amount]", formatMoney(amount));
        if (msgDefault) msgDefault.classList.add("active");
        if (msgValueEl) msgValueEl.innerHTML = message;
        if (msgSuccess) msgSuccess.classList.remove("active");
      } else {
        if (msgDefault) msgDefault.classList.remove("active");
        if (msgSuccess) msgSuccess.classList.add("active");
      }

      progressEl.style.width = progress + "%";
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => (response.ok ? response.text() : null))
      .then((responseText) => {
        if (!responseText) return;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        if (!sourceQty) return;
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-header",
        section: document.getElementById("main-cart-header")?.dataset?.id,
        selector: ".js-contents-heading",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset?.id,
        selector: ".js-contents-totals",
      },
      {
        id: "main-cart-shipping-goal",
        section:
          document.getElementById("main-cart-shipping-goal")?.dataset?.id ||
          null,
        selector: ".js-contents-shipping-goal",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    if (document.querySelector(".js-shipping-goal-progress")) {
      let progressPrev = getComputedStyle(
        document.querySelector(".js-shipping-goal-progress")
      ).getPropertyValue("width");
      document.documentElement.style.setProperty(
        "--shipping-goal-progress-prev",
        progressPrev
      );
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: parsedState.errors,
                message: parsedState.errors,
              },
            })
          );
          // [End theme custom event] - Cart error event

          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartPageWrapper = document.querySelector(".main-cart__wrapper");
        const parentCartDrawer = this.closest("cart-drawer");

        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }
        if (cartPageWrapper) {
          cartPageWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }
        if (parentCartDrawer) {
          parentCartDrawer.productId = parsedState.items[0]?.product_id;
        }

        const sectionsToRender = this.getSectionsToRender();
        const sections = await resolveCartSections(
          parsedState,
          sectionsToRender.map((section) => section.section)
        );

        sectionsToRender.forEach((section) => {
          const sectionHTML = sections?.[section.section];
          if (!sectionHTML) return;
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (!elementToReplace) return;
          const updatedHTML = this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );
          if (updatedHTML === null) return;
          elementToReplace.innerHTML = updatedHTML;
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;

        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".cart-drawer__empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });

        // [Start theme custom event] - Cart item change event
        document.dispatchEvent(
          new CustomEvent("line-item:change", {
            detail: {
              lineItem: parsedState.items[line - 1] || null,
              cart: parsedState,
            },
          })
        );
        // [End theme custom event] - Cart item change event

        // [Start theme custom event] - Cart change event
        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: {
              cart: parsedState,
            },
          })
        );
        // [End theme custom event] - Cart change event
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (document.querySelector(".cart-shipping-goal")) {
          this.initCartShippingGoal();
        }
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  enableLoading(line) {
    this.classList.add("cart-items-disabled");

    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);
    if (cartLine) {
      cartLine.classList.add("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    const cartFooter =
      document.querySelector(".main-cart__footer") ||
      document.querySelector(".cart-drawer__footer");
    if (cartFooter) {
      const totalValue =
        cartFooter.querySelector(".main-cart__total-value") ||
        cartFooter.querySelector(".cart-drawer__total-value");
      if (totalValue) totalValue.classList.add("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    this.classList.remove("cart-items-disabled");

    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);
    if (cartLine) {
      cartLine.classList.remove("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }

    const cartFooter =
      document.querySelector(".main-cart__footer") ||
      document.querySelector(".cart-drawer__footer");
    if (cartFooter) {
      const totalValue =
        cartFooter.querySelector(".main-cart__total-value") ||
        cartFooter.querySelector(".cart-drawer__total-value");
      if (totalValue) totalValue.classList.remove("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");
    if (this.header) this.header.preventHide = false;

    this.setHeaderCartIconAccessibility();
    this.addEventListener("keyup", (event) => {
      event.code === "Escape" && this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".js-trigger-cart-drawer");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        if (!this.getAttribute("open")) {
          this.open(cartLink);
        } else {
          this.close();
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.getRecommendationsProducts();

    this.setAttribute("open", true);

    trapFocus(this, this.querySelector(".cart-drawer__inner"));

    document.body.classList.add("overflow-hidden-drawer");
  }

  close() {
    this.removeAttribute("open");
    this.productId = "";

    let restoreFocusTo = null;

    if (this.activeElement) {
      let activeElement = this.activeElement;
      const productCard = this.activeElement.closest("product-card");
      const productCardLink = productCard?.querySelector(
        ".product-card__media-link"
      );
      if (productCardLink) activeElement = productCardLink;

      restoreFocusTo = activeElement;
      removeTrapFocus(activeElement);
    }

    const openModal = document.querySelector("modal-dialog[open]");
    if (openModal) {
      const focusTarget = openModal.contains(restoreFocusTo)
        ? restoreFocusTo
        : openModal.querySelector('[role="dialog"]');
      trapFocus(openModal, focusTarget);
    }

    const innerModals = this.querySelectorAll("cart-drawer-inner-modal");
    innerModals.forEach((innerModal) => {
      if (typeof innerModal.onClose === "function") {
        innerModal.onClose();
      }
    });

    document.body.classList.remove("overflow-hidden-drawer");

    if (this.header) this.header.preventHide = false;
  }

  renderContents(parsedState) {
    if (this.classList.contains("is-empty")) {
      this.classList.remove("is-empty");
    }
    this.productId = parsedState.product_id;

    const sectionsToRender = this.getSectionsToRender();

    resolveCartSections(
      parsedState,
      sectionsToRender.map((section) => section.section)
    ).then((sections) => {
      sectionsToRender.forEach((section) => {
        const sectionHTML = sections?.[section.section];
        if (!sectionHTML) return;
        const elementToReplace =
          document
            .getElementById(section.id)
            ?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!elementToReplace) return;
        const updatedHTML = this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );
        if (updatedHTML === null) return;
        elementToReplace.innerHTML = updatedHTML;
      });

      setTimeout(() => {
        if (this.hasAttribute("data-auto-open")) {
          this.open();
        }
      });
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getRecommendationsProducts() {
    const recommendationsEl = this.querySelector(
      ".cart-drawer__recommendations"
    );
    if (!recommendationsEl) return;

    let fetchUrl = recommendationsEl.dataset.fetchUrl;
    if (!fetchUrl) return;

    if (this.productId) {
      const baseUrl = recommendationsEl.dataset.baseUrl;
      if (baseUrl) {
        fetchUrl = baseUrl.replace("[product_id]", this.productId);
      }
    }

    const oldList = recommendationsEl.querySelector(
      ".cart-drawer__recommendations-list"
    );
    if (oldList) oldList.remove();

    recommendationsEl.hidden = true;

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendationsList = html.querySelector(
          ".cart-drawer__recommendations-list"
        );
        if (!recommendationsList || !recommendationsList.children.length)
          return;

        recommendationsEl.appendChild(recommendationsList);
        recommendationsEl.hidden = false;
        trapFocus(this, this.querySelector(".cart-drawer__inner"));
      })
      .catch((error) => {
        console.error("Failed to load recommendations products:", error);
      });
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          const parentDrawer = this.closest("cart-drawer");
          if (
            parentDrawer &&
            typeof parentDrawer.getRecommendationsProducts === "function"
          ) {
            parentDrawer.getRecommendationsProducts();
          }
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}

class CartNote extends HTMLElement {
  constructor() {
    super();
    this.spinnerIcon = this.querySelector(".cart-note__loading");
    this.successIcon = this.querySelector(".cart-note__success");
    this.textarea = this.querySelector("textarea");
    this.submitBtn = this.querySelector(".cart-note__submit");
    this.cartButtons =
      document.querySelector(".main-cart__buttons") ||
      document.querySelector(".cart-drawer__buttons");

    this.value = this.textarea.value;
    this.savedValue = this.value;

    // With a Save button (cart drawer) the note is sent on click only; without one
    // (cart page) it keeps saving on input.
    if (this.submitBtn) {
      this.textarea?.addEventListener("input", (event) => {
        this.value = event.target.value;
      });

      this.submitBtn.addEventListener("click", this.onSubmitForm.bind(this));
    } else {
      this.textarea?.addEventListener(
        "input",
        debounce((event) => {
          this.value = event.target.value;

          this.onSubmitForm(event);
        }, 300)
      );
    }
  }

  onSubmitForm(event) {
    event.preventDefault();

    if (event.type === "click" && this.value === this.savedValue) {
      this.closeInnerModal();
      return;
    }

    this.enableLoading();

    const body = JSON.stringify({ note: this.value });

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        this.savedValue = this.value;

        if (data.note !== "") {
          console.info(`Order note "${data.note}" saved successfully.`);
        } else {
          console.info("Order note removed successfully.");
        }

        if (this.successIcon) this.successIcon.style.display = "flex";

        setTimeout(() => {
          if (this.successIcon) this.successIcon.style.display = "none";
        }, 2000);

        this.closeInnerModal();
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      })
      .finally(() => {
        this.disableLoading();
        this.updateHtmlStatus();
      });
  }

  closeInnerModal() {
    const innerModal = this.closest("cart-drawer-inner-modal");
    if (typeof innerModal?.onClose === "function") innerModal.onClose();
  }

  enableLoading() {
    if (this.spinnerIcon) this.spinnerIcon.style.display = "flex";
    if (this.successIcon) this.successIcon.style.display = "none";
    if (this.submitBtn) {
      this.submitBtn.classList.add("loading");
      const loader = this.submitBtn.querySelector(".button-loader");
      if (loader) loader.classList.remove("hidden");
    }
    if (this.cartButtons) {
      this.cartButtons.style.pointerEvents = "none";
      this.cartButtons.style.opacity = "0.7";
    }
  }

  disableLoading() {
    if (this.spinnerIcon) this.spinnerIcon.style.display = "none";
    if (this.submitBtn) {
      this.submitBtn.classList.remove("loading");
      const loader = this.submitBtn.querySelector(".button-loader");
      if (loader) loader.classList.add("hidden");
    }
    if (this.cartButtons) {
      this.cartButtons.removeAttribute("style");
    }
  }

  updateHtmlStatus() {
    const noteStatusElements = document.querySelectorAll(
      ".js-cart-note-status"
    );

    const addLabel =
      document.querySelector("[data-cart-note-add-label]")?.textContent ||
      "Add Order Note";
    const editLabel =
      document.querySelector("[data-cart-note-edit-label]")?.textContent ||
      "Edit Order Note";

    noteStatusElements.forEach((el) => {
      if (this.value.trim() === "") {
        el.textContent = addLabel;
        el.dataset.label = addLabel;
      } else {
        el.textContent = editLabel;
        el.dataset.label = editLabel;
      }
    });
  }
}

if (!customElements.get("cart-note")) {
  customElements.define("cart-note", CartNote);
}

class CartDrawerInnerModal extends HTMLElement {
  constructor() {
    super();

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
  }

  connectedCallback() {
    this.modalEl = this.querySelector(".cart-drawer-inner-modal__window");
    this.openBtn = this.querySelector(".cart-drawer-inner-modal__open");
    this.closeBtn = this.querySelector(".cart-drawer-inner-modal__close");
    this.overlayEl = this.closest(".cart-drawer__inner")?.querySelector(
      "[data-inner-modal-overlay]"
    );
    this.focusElement =
      this.querySelector("textarea") || this.querySelector("input");

    this.openBtn?.addEventListener("click", this.onOpen);
    this.closeBtn?.addEventListener("click", this.onClose);
    this.overlayEl?.addEventListener("click", this.onClose);
    this.modalEl?.addEventListener("transitionend", this.onTransitionEnd);
  }

  disconnectedCallback() {
    this.openBtn?.removeEventListener("click", this.onOpen);
    this.closeBtn?.removeEventListener("click", this.onClose);
    this.overlayEl?.removeEventListener("click", this.onClose);
    this.modalEl?.removeEventListener("transitionend", this.onTransitionEnd);
  }

  onOpen() {
    document.querySelectorAll("cart-drawer-inner-modal").forEach((modal) => {
      if (modal !== this && typeof modal.onClose === "function") {
        modal.onClose();
      }
    });

    this.modalEl?.setAttribute("open", true);
  }

  onClose() {
    this.modalEl?.removeAttribute("open");
  }

  onTransitionEnd(event) {
    if (event.target !== this.modalEl) return;

    if (this.modalEl.hasAttribute("open")) {
      trapFocus(this.modalEl, this.focusElement);
      return;
    }

    const cartDrawer = this.closest("cart-drawer");
    if (!cartDrawer) {
      removeTrapFocus(this.openBtn);
      return;
    }

    if (cartDrawer.hasAttribute("open")) {
      trapFocus(cartDrawer, this.openBtn);
    }
  }
}

if (!customElements.get("cart-drawer-inner-modal")) {
  customElements.define("cart-drawer-inner-modal", CartDrawerInnerModal);
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector(".header-wrapper");
    if (this.header) this.header.preventHide = false;

    this.itemEl = this.querySelector("#cart-notification-item");

    this._onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("keyup", (event) => {
      event.code === "Escape" && this.close();
    });
    document.addEventListener("click", this._onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onOutsideClick);
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.setAttribute("open", true);

    this.addEventListener(
      "transitionend",
      () => {
        if (this.getAttribute("open")) {
          const containerTrapFocus =
            document.getElementById("CartNotification");
          const focusElement = this.querySelector(".cart-notification__inner");
          trapFocus(containerTrapFocus, focusElement);
        }
      },
      { once: true }
    );

    if (this.closeSetTimeout) clearTimeout(this.closeSetTimeout);
    this.closeSetTimeout = setTimeout(() => {
      if (this.getAttribute("open")) {
        this.close();
      }
    }, 8000);
  }

  close() {
    this.removeAttribute("open");
    this.productId = "";

    if (this.closeSetTimeout) {
      clearTimeout(this.closeSetTimeout);
      this.closeSetTimeout = null;
    }

    if (this.activeElement) {
      let activeElement = this.activeElement;
      const productCard = this.activeElement.closest("product-card");
      const productCardLink = productCard?.querySelector(
        ".product-card__media-link"
      );
      if (productCardLink) activeElement = productCardLink;

      removeTrapFocus(activeElement);
    }

    if (this.header) this.header.preventHide = false;
    this.cartItemKey = "";

    this.addEventListener(
      "transitionend",
      () => {
        if (!this.getAttribute("open")) {
          if (this.itemEl) this.itemEl.innerHTML = "";
        }
      },
      { once: true }
    );
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    const sectionsToRender = this.getSectionsToRender();

    resolveCartSections(
      parsedState,
      sectionsToRender.map((section) => section.section)
    ).then((sections) => {
      if (!sections) return;

      sectionsToRender.forEach((section) => {
        const sectionHTML = sections[section.section];
        if (!sectionHTML) return;

        if (section.section === "cart-notification-items") {
          const addedItemSource = this.getParsedElement(
            sectionHTML,
            section.selector
          );

          if (addedItemSource && this.itemEl) {
            this.itemEl.innerHTML = "";
            this.itemEl.append(addedItemSource);
          }
          return;
        }

        const elementToReplace =
          document
            .getElementById(section.id)
            ?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!elementToReplace) return;
        const updatedHTML = this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );
        if (updatedHTML === null) return;
        elementToReplace.innerHTML = updatedHTML;
      });

      setTimeout(() => {
        this.open();
      });
    });
  }

  getParsedElement(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner",
      },
      {
        id: "CartNotification",
        section: "cart-notification-items",
        selector: `[id="CartNotification-Item-${this.cartItemKey}"]`,
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  onOutsideClick(event) {
    if (this.getAttribute("open") !== "true" || this.contains(event.target)) {
      return;
    }
    this.close();
  }
}

if (!customElements.get("cart-notification")) {
  customElements.define("cart-notification", CartNotification);
}

class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    /** @type {AbortController | null} */
    this.activeFetch = null;
  }

  connectedCallback() {
    this.#initRefs();
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[name="discount"]');
    this.submitBtn = this.querySelector('button[type="submit"]');

    this.form?.addEventListener("submit", this.applyDiscount);
    this.input?.addEventListener("input", this.resetDiscountError);
    this.addEventListener("click", this.removeDiscount);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.applyDiscount);
    this.input?.removeEventListener("input", this.resetDiscountError);
    this.removeEventListener("click", this.removeDiscount);

    if (this.activeFetch) {
      this.activeFetch.abort();
      this.activeFetch = null;
    }
  }

  #initRefs() {
    this.refs = {};
    for (const el of this.querySelectorAll("[ref]")) {
      const name = el.getAttribute("ref");
      if (name && el instanceof HTMLElement) this.refs[name] = el;
    }
  }

  #createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }

    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount to the cart.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;

    if (
      !(form instanceof HTMLFormElement) ||
      !(this.input instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const discountCodeValue = this.input.value.trim();
    if (!discountCodeValue) {
      this.#handleDiscountError("empty");
      this.input.focus();
      return;
    }

    const abortController = this.#createAbortController();

    if (this.submitBtn) this.submitBtn.classList.add("loading");
    const btnLoader = this.submitBtn.querySelector(".button-loader");
    if (btnLoader) btnLoader.classList.remove("hidden");

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (
        existingDiscounts.some(
          (code) => code.toUpperCase() === discountCodeValue.toUpperCase()
        )
      ) {
        return;
      }

      this.#clearDiscountErrors();

      const body = JSON.stringify({
        discount: [...existingDiscounts, discountCodeValue].join(","),
        sections: [this.dataset.sectionId],
      });

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        ...{ body },
        signal: abortController.signal,
      });

      const data = await response.json();

      // If the discount code is not applicable, an error is displayed and the function stops
      if (!this.#checkApplicableCode(data.discount_codes, discountCodeValue)) {
        return;
      }

      const sourceSection = this.#getNewSectionHtml(data.sections);

      if (sourceSection) {
        // If the discount code applies to shipping, an error is displayed and the function stops
        if (
          !this.#checkShippingDiscountCode(
            data.discount_codes,
            discountCodeValue,
            sourceSection
          )
        ) {
          return;
        }

        this.#updateSectionHtml(sourceSection);
      }
    } catch (error) {
      console.error("Failed to apply discount:", error);
    } finally {
      this.activeFetch = null;
      if (this.submitBtn) this.submitBtn.classList.remove("loading");
      if (btnLoader) btnLoader.classList.add("hidden");
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    if (
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const pill = event.target.closest(".cart-discount-code__pill");
    if (!(pill instanceof HTMLLIElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    if (this.submitBtn) this.submitBtn.classList.add("loading");
    const btnLoader = this.submitBtn.querySelector(".button-loader");
    if (btnLoader) btnLoader.classList.remove("hidden");

    try {
      const body = JSON.stringify({
        discount: existingDiscounts.join(","),
        sections: [this.dataset.sectionId],
      });

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        ...{ body },
        signal: abortController.signal,
      });

      const data = await response.json();

      const sourceSection = this.#getNewSectionHtml(data.sections);
      this.#updateSectionHtml(sourceSection);
    } catch (error) {
      console.error("Failed to remove discount:", error);
    } finally {
      this.activeFetch = null;
      if (this.submitBtn) this.submitBtn.classList.remove("loading");
      if (btnLoader) btnLoader.classList.add("hidden");
    }
  };

  /**
   * Handles the discount error.
   * @param {'empty' | 'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountField,
      cartDiscountError,
      cartDiscountErrorEmpty,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    this.#clearDiscountErrors();

    let target = cartDiscountErrorShipping;
    if (type === "empty") target = cartDiscountErrorEmpty;
    if (type === "discount_code") target = cartDiscountErrorDiscountCode;

    cartDiscountField?.classList.add("field-frame--error");
    this.input?.setAttribute("aria-invalid", "true");
    cartDiscountError?.classList.remove("hidden");
    target?.classList.remove("hidden");
  }

  /**
   * Resets the field error state.
   */
  resetDiscountError = () => {
    this.#clearDiscountErrors();
  };

  #clearDiscountErrors() {
    const { cartDiscountField, cartDiscountError } = this.refs;

    cartDiscountField?.classList.remove("field-frame--error");
    this.input?.removeAttribute("aria-invalid");
    cartDiscountError?.classList.add("hidden");

    for (const message of this.querySelectorAll(
      ".cart-discount-code__error-text"
    )) {
      message.classList.add("hidden");
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll(".cart-discount-code__pill");
    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === "string"
      ) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Checks if a discount code is applicable.
   * @param {Array} cartDiscountCodes - The array of discount codes in the cart.
   * @param {string} discountCodeValue - The discount code to check.
   * Returns boolean indicating if the discount code is applicable.
   * @returns {boolean}
   */
  #checkApplicableCode(cartDiscountCodes, discountCodeValue) {
    if (
      cartDiscountCodes.find((discount) => {
        return (
          discount.code === discountCodeValue && discount.applicable === false
        );
      })
    ) {
      this.input.value = "";
      this.#handleDiscountError("discount_code");
      return false;
    }
    return true;
  }

  /**
   * Checks if a discount code is applicable.
   * @param {Array} cartDiscountCodes - The array of discount codes in the cart.
   * @param {string} discountCodeValue - The discount code to check.
   * @param {HTMLElement} sourceSection - The section element containing discount codes.
   * Returns boolean indicating if the discount code is applicable.
   * @returns {boolean}
   */
  #checkShippingDiscountCode(
    cartDiscountCodes,
    discountCodeValue,
    sourceSection
  ) {
    const discountCodes =
      sourceSection?.querySelectorAll(".cart-discount-code__pill") || [];
    const existingDiscounts = this.#existingDiscounts();

    const codes = Array.from(discountCodes)
      .map((element) =>
        element instanceof HTMLLIElement ? element.dataset.discountCode : null
      )
      .filter(Boolean);
    if (
      codes.length === existingDiscounts.length &&
      codes.every((code) => existingDiscounts.includes(code)) &&
      cartDiscountCodes.find((discount) => {
        return (
          discount.code === discountCodeValue && discount.applicable === true
        );
      })
    ) {
      this.#handleDiscountError("shipping");
      this.input.value = "";
      return false;
    }
    return true;
  }

  #getNewSectionHtml(cartSections) {
    const newHtml = cartSections[this.dataset.sectionId];
    const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");

    const sectionId =
      this.dataset.sectionId === "cart-drawer"
        ? "CartDrawer"
        : `shopify-section-${this.dataset.sectionId}`;
    const sourceSection = parsedHtml.getElementById(sectionId);

    return sourceSection;
  }

  #updateSectionHtml(sourceSection) {
    const sectionId = `shopify-section-${this.dataset.sectionId}`;
    const targetSection =
      this.dataset.sectionId === "cart-drawer"
        ? document.getElementById("CartDrawer")
        : document.getElementById(sectionId);
    if (sourceSection && targetSection) {
      targetSection.innerHTML = sourceSection.innerHTML;
    }
  }
}

if (!customElements.get("cart-discount-code")) {
  customElements.define("cart-discount-code", CartDiscountCode);
}
